import { Component, EventEmitter, HostBinding, Input, OnDestroy, Output, ChangeDetectionStrategy } from '@angular/core';
import { Router } from "@angular/router";
import { DataStorageService } from "../../../services/data-storage.service";
import { PaymentFrequency, PetInsuranceData } from "../../../models";
import { Subject, takeUntil } from "rxjs";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: 'cpf-insurance-overview',
  templateUrl: './insurance-overview.component.html',
  styleUrls: ['./insurance-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class InsuranceOverviewComponent implements OnDestroy {
  @Input() orientation: string = 'vertical';
  @Input() desktopVersion = false;

  @Output() editPetInfoClicked = new EventEmitter<void>();
  @Output() editInsuranceInfoClicked = new EventEmitter<void>();
  @Output() openDetailsClicked = new EventEmitter<void>();

  public isContractActive = false;
  public petData?: PetInsuranceData;
  private destroy$ = new Subject<void>();

  constructor(private router: Router,
              private dataStorageService: DataStorageService,
              private translate: TranslateService) {
    this.setPetData();

    this.dataStorageService.selectedData$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((data) => {
      if (data) {
        this.petData = data;
        this.setIsContractActive();
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostBinding('class')
  get orientationClass() {
    return `orientation-${this.orientation}`;
  }

  setIsContractActive() {
    if (!this.petData) {
      return
    }

    const startDate = new Date(this.petData.start_date);
    const endDate = new Date(this.petData.end_date);

    if (startDate.getTime() < new Date().getTime() && endDate.getTime() > new Date().getTime()) {
      this.isContractActive = true;
    } else {
      this.isContractActive = false;
    }
  }

  setPetData() {
    const selectedData = this.dataStorageService.selectedData;

    if (selectedData) {
      this.petData = selectedData;
    }
  }

  getSubscriptionInterval() {
    const values = Object.values(PaymentFrequency);
    const index = values.indexOf(this.petData!.payment_interval) || 0;
    const interval = Object.keys(PaymentFrequency)[index];
    const intervalLabel = this.translate.instant(`common.subscription-interval.${interval}`);
    return intervalLabel;
  }

  getTotalPrice() {
    return this.petData ? this.petData.yearly_gross_premium : 0;
  }

  openDetails() {
    if (this.openDetailsClicked.observed) {
      this.openDetailsClicked.emit();
      return;
    }
    this.router.navigate(['calingo', 'pet', 'insurance', 'details'])
  }

  editPetInfo() {
    if (this.editPetInfoClicked.observed) {
      this.editPetInfoClicked.emit();
      return;
    }
    this.router.navigate(['calingo', 'pet', 'insurance', 'edit-pet-info'])
  }

  getBundleName() {
    switch (this.petData?.product_bundle) {
      case 'L':
        return 'insurance-overview.bundle.L';
      case 'M':
        return 'insurance-overview.bundle.M';
      case 'S':
        return 'insurance-overview.bundle.S';
      case 'A':
        return 'insurance-overview.bundle.A';
      default:
        return 'insurance-overview.bundle.L';
    }
  }

  showCurrentDeductible() {
    if (!this.petData) {
      return
    }

    const petFullYears = (new Date().getTime() - new Date(this.petData.birthdate).getTime()) / (1000 * 60 * 60 * 24 * 365);
    const deductibles = petFullYears < 9 ? this.petData.deductible_percentage_until_9_years : this.petData.deductible_percentage_after_9_years;
    return (100 - deductibles).toFixed(0);
  }

  getBreed() {
    if (!this.petData || !this.dataStorageService.catBreeds || !this.dataStorageService.dogBreeds || !this.dataStorageService.catBreeds.length || !this.dataStorageService.dogBreeds.length) {
      return ""
    }

    const breeds = this.petData.pet_species === 'cat' ? this.dataStorageService.catBreeds : this.dataStorageService.dogBreeds;

    const findBreed = breeds.find(breed => breed.id===this.petData!.breed);

    if (findBreed){
      return findBreed.name;
    } else {
      console.warn("BREED NOT FOUND FOR ID: " + this.petData!.breed + "")
      return breeds[0].name;
    }
  }
}
